/**
 * Mercurian Equity Brand Configuration
 * Colors, fonts, layout, and contact information for the website.
 */

export interface BrandConfig {
  // Core Brand Identity
  siteName: string;
  tagline: string;
  description: string;

  // Technical Configuration (removed: tenantId, apiUrl, brandIdentifier)

  // Logo and Images
  logo: {
    src: string;
    alt: string;
    favicon: string;
  };

  // Contact Information
  contact: {
    businessType: string;
    industry: string;
    email?: string;
    phone?: string;
    address?: string;
  };

  // Color Palette
  colors: {
    accentBlue?: string;
    badges?: any;
    // Primary brand colors
    primary: string;
    primaryHover: string;
    secondary: string;
    secondaryHover: string;

    // Accent colors
    accent: string;
    accentLight: string;

    // Background colors
    background: {
      main: string;
      light: string;
      card: string;
      overlay: string;
      surface: string;
      darkSurface?: string;
      cardGradient?: string;
      darkOverlay?: string;
    };

    // Status colors
    status: {
      success: string;
      warning: string;
      error: string;
      info: string;
    };

    // Text colors
    text: {
      primary: string;
      secondary: string;
      muted: string;
      inverse: string;
      primaryDark?: string;
      secondaryDark?: string;
      mutedDark?: string;
    };

    // Border colors
    border: {
      light: string;
      medium: string;
      dark: string;
      darkCard?: string;
    };
  };

  // Typography
  fonts: {
    heading: string;
    body: string;
  };

  // Component styling
  components: {
    button: {
      primaryBg: string;
      primaryHover: string;
      secondaryBg: string;
      secondaryHover: string;
      whiteBg?: string;
      whiteText?: string;
      whiteHover?: string;
    };
    menu?: {
      bg: string;
      itemBg: string;
      itemColor: string;
      itemHoverBg: string;
      itemHoverColor: string;
      borderColor: string;
    };
    card: {
      bg: string;
      border: string;
      shadow: string;
      darkShadow?: string;
      backdropFilter?: string;
    };
    navbar: {
      bg: string;
      text: string;
      shadow: string;
    };
    footer: {
      bg: string;
      text: string;
      linkColor: string;
      linkHover: string;
    };
    form: {
      bg: string;
      fieldBg: string;
      fieldBorder: string;
      fieldBorderFocus: string;
      fieldShadow: string;
      fieldShadowFocus: string;
      labelColor: string;
      placeholderColor: string;
    };
  };

}

// Mercurian Equity Configuration
export const brandConfig: BrandConfig = {
  // Core Brand Identity
  siteName: "Mercurian Equity",
  tagline: "Guided by market intelligence, driven by opportunity.",
  description: "Mercurian Equity is an investment management firm, focused on delivering investment solutions and strategic capital partnerships for global institutions and wholesale clients.",

  // Technical Configuration

  // Logo and Images
  logo: {
    src: "🔷", // Clean geometric icon
    alt: "Mercurian Equity Logo",
    favicon: "%PUBLIC_URL%/favicon.ico"
  },

  // Contact Information
  contact: {
    businessType: "Investment Management",
    industry: "Finance",
    email: "info@mercurianequity.com",
    phone: "",
    address: "Level 28, 140 St Georges Terrace, Perth, Western Australia, 6000"
  },

  // Color Palette - Citrus Splash Vibrant Design
  colors: {
    // Primary brand colors (Soft Purple)
    primary: "#CB6BE6",        // Soft purple - bold & innovative
    primaryHover: "#B355D9",   // Darker purple on hover
    secondary: "#FFA500",      // Orange - energetic & warm
    secondaryHover: "#E6940A", // Darker orange

    // Accent colors (Citrus gradients)
    accent: "#FF4500",         // Orange-red accent
    accentLight: "#FFD700",    // Gold accent

    // Background colors (Light with citrus energy)
    background: {
      main: "#FFFFFF",         // Pure white background
      light: "#FFFEF8",        // Very light warm (subtle yellow tint)
      card: "#FFFFFF",         // White cards
      overlay: "rgba(203, 107, 230, 0.05)", // Subtle purple overlay
      surface: "#FFFFFF",      // White surface
      // Light theme surface variants
      darkSurface: "#FFF8F0",  // Light warm surface
      cardGradient: "linear-gradient(135deg, rgba(203, 107, 230, 0.03) 0%, rgba(255, 165, 0, 0.03) 25%, rgba(255, 69, 0, 0.03) 50%, rgba(255, 215, 0, 0.03) 75%, rgba(255, 255, 0, 0.03) 100%)",
      darkOverlay: "rgba(255, 254, 248, 0.95)" // Light warm overlay
    },

    // Status colors (Citrus-inspired)
    status: {
      success: "#32CD32",      // Lime green (citrus family)
      warning: "#FFD700",      // Gold (from palette)
      error: "#FF4500",        // Orange-red (from palette)
      info: "#CB6BE6"          // Primary purple
    },

    // Text colors (High contrast for vibrant theme)
    text: {
      primary: "#2D2D2D",      // Very dark gray for primary text
      secondary: "#5D5D5D",    // Medium dark gray for secondary
      muted: "#8D8D8D",        // Medium gray for muted text
      inverse: "#FFFFFF",      // White text for dark elements
      // Light theme text colors
      primaryDark: "#2D2D2D",
      secondaryDark: "rgba(45, 45, 45, 0.8)",
      mutedDark: "rgba(45, 45, 45, 0.5)"
    },

    // Border colors (Warm & energetic)
    border: {
      light: "rgba(255, 215, 0, 0.2)",     // Light gold border
      medium: "rgba(255, 165, 0, 0.3)",    // Orange border  
      dark: "rgba(255, 69, 0, 0.4)",       // Orange-red border
      // Light theme card borders
      darkCard: "rgba(203, 107, 230, 0.1)"
    },
    
    // Additional colors
    accentBlue: "#FFFF00",    // Bright yellow accent (from palette)
    
    // Badge colors with transparency (Citrus-inspired)
    badges: {
      blue: {
        bg: "rgba(203, 107, 230, 0.1)",
        color: "#CB6BE6",
        border: "rgba(203, 107, 230, 0.2)"
      },
      green: {
        bg: "rgba(50, 205, 50, 0.1)",
        color: "#32CD32",
        border: "rgba(50, 205, 50, 0.2)"
      },
      purple: {
        bg: "rgba(203, 107, 230, 0.1)",
        color: "#CB6BE6",
        border: "rgba(203, 107, 230, 0.2)"
      },
      yellow: {
        bg: "rgba(255, 255, 0, 0.1)",
        color: "#FFFF00",
        border: "rgba(255, 255, 0, 0.2)"
      },
      gray: {
        bg: "rgba(93, 93, 93, 0.1)",
        color: "#5D5D5D",
        border: "rgba(93, 93, 93, 0.2)"
      },
      orange: {
        bg: "rgba(255, 165, 0, 0.1)",
        color: "#FFA500",
        border: "rgba(255, 165, 0, 0.2)"
      },
      teal: {
        bg: "rgba(255, 215, 0, 0.1)",
        color: "#FFD700",
        border: "rgba(255, 215, 0, 0.2)"
      },
      cyan: {
        bg: "rgba(255, 69, 0, 0.1)",
        color: "#FF4500",
        border: "rgba(255, 69, 0, 0.2)"
      },
      pink: {
        bg: "rgba(203, 107, 230, 0.1)",
        color: "#CB6BE6",
        border: "rgba(203, 107, 230, 0.2)"
      }
    }
  },

  // Typography (Charter serif fonts matching logo)
  fonts: {
    heading: "Charter, ui-serif, Georgia, Cambria, 'Times New Roman', Times, serif",
    body: "Moderat, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif, \"Apple Color Emoji\", \"Segoe UI Emoji\", Segoe UI Symbol, \"Noto Color Emoji\""
  },

  // Component styling
  components: {
    button: {
      primaryBg: "#CB6BE6",
      primaryHover: "#B355D9",
      secondaryBg: "rgba(255, 165, 0, 0.1)",
      secondaryHover: "rgba(255, 165, 0, 0.15)",
      // Light button styles for light backgrounds
      whiteBg: "#FFFFFF",
      whiteText: "#CB6BE6",
      whiteHover: "#FFFEF8"
    },
    menu: {
      bg: "rgba(255, 255, 255, 0.98)",        // Light background
      itemBg: "transparent",                   // Transparent item background
      itemColor: "#5D5D5D",                   // Dark gray text
      itemHoverBg: "rgba(203, 107, 230, 0.1)",  // Light purple hover background
      itemHoverColor: "#CB6BE6",              // Primary purple text on hover
      borderColor: "rgba(255, 165, 0, 0.3)"  // Orange border
    },
    card: {
      bg: "#FFFFFF",
      border: "rgba(255, 215, 0, 0.2)",
      shadow: "0 1px 3px rgba(255, 69, 0, 0.1), 0 1px 2px rgba(255, 165, 0, 0.06)",
      // Light theme card styles
      darkShadow: "0 4px 25px 0 rgba(203, 107, 230, 0.08)",
      backdropFilter: "blur(8px)"
    },
    navbar: {
      bg: "transparent",
      text: "#FFFFFF",
      shadow: "none"
    },
    footer: {
      bg: "#FFFEF8",
      text: "#5D5D5D",
      linkColor: "#CB6BE6",
      linkHover: "#B355D9"
    },
    form: {
      bg: "#FFFFFF",
      fieldBg: "#FFFEF8",
      fieldBorder: "rgba(255, 165, 0, 0.3)",
      fieldBorderFocus: "#CB6BE6",
      fieldShadow: "0 1px 2px rgba(255, 69, 0, 0.05)",
      fieldShadowFocus: "0 0 0 3px rgba(203, 107, 230, 0.1), 0 1px 2px rgba(255, 69, 0, 0.05)",
      labelColor: "#2D2D2D",
      placeholderColor: "#8D8D8D"
    }
  },

};

// Helper functions for easy access
export const getColor = (path: string): string => {
  const keys = path.split(".");
  let value: any = brandConfig.colors;

  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Color path "${path}" not found in brand config`);
      return "#007AFF"; // Fallback to primary
    }
  }

  return value;
};

export const getBrandValue = (path: string): any => {
  const keys = path.split(".");
  let value: any = brandConfig;

  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Brand config path "${path}" not found`);
      return null;
    }
  }

  return value;
};

export const getComponent = (component: string, property: string): string => {
  const componentConfig = (brandConfig.components as any)[component];
  if (!componentConfig) {
    console.warn(`Component "${component}" not found in brand config`);
    return "#007AFF";
  }

  const value = componentConfig[property];
  if (!value) {
    console.warn(`Property "${property}" not found for component "${component}"`);
    return "#007AFF";
  }

  return value;
};

// CSS Custom Properties Generator
export const generateCSSCustomProperties = (): string => {
  const { colors } = brandConfig;

  return `
    :root {
      --brand-primary: ${colors.primary};
      --brand-primary-hover: ${colors.primaryHover};
      --brand-secondary: ${colors.secondary};
      --brand-secondary-hover: ${colors.secondaryHover};
      --brand-accent: ${colors.accent};
      --brand-accent-light: ${colors.accentLight};
      
      --brand-bg-main: ${colors.background.main};
      --brand-bg-light: ${colors.background.light};
      --brand-bg-card: ${colors.background.card};
      --brand-bg-overlay: ${colors.background.overlay};
      --brand-bg-surface: ${colors.background.surface};
      
      --brand-success: ${colors.status.success};
      --brand-warning: ${colors.status.warning};
      --brand-error: ${colors.status.error};
      --brand-info: ${colors.status.info};
      
      --brand-text-primary: ${colors.text.primary};
      --brand-text-secondary: ${colors.text.secondary};
      --brand-text-muted: ${colors.text.muted};
      --brand-text-inverse: ${colors.text.inverse};
      
      --brand-border-light: ${colors.border.light};
      --brand-border-medium: ${colors.border.medium};
      --brand-border-dark: ${colors.border.dark};
      
      --brand-font-heading: ${brandConfig.fonts.heading};
      --brand-font-body: ${brandConfig.fonts.body};
    }
  `;
};

export default brandConfig; 